import React from 'react';
import * as Icons from 'lucide-react';

export interface MetricWidgetConfig {
  title: string;
  value: number;
  previousValue?: number;
  unit: string;
  icon: string;
  color: string;
  backgroundColor?: string;
}

interface MetricWidgetProps {
  config: MetricWidgetConfig;
  width: number;  // Grid columns (1-12)
  height: number; // Grid rows
}

function formatNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1000000000) return (value / 1000000000).toFixed(1) + 'B';
  if (abs >= 1000000) return (value / 1000000).toFixed(1) + 'M';
  if (abs >= 1000) return (value / 1000).toFixed(1) + 'K';
  return value.toLocaleString();
}

// Calculate if background is light or dark
function isLightBackground(hexColor?: string): boolean {
  if (!hexColor) return true; // Default to light
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155; // >155 = light background
}

export function MetricWidget({ config, width, height }: MetricWidgetProps) {
  const { title = 'Untitled', value = 0, previousValue, unit = '#', icon = 'TrendingUp', color = '#10b981', backgroundColor } = config || {};
  const IconComponent = (Icons as any)[icon] || Icons.TrendingUp;

  const comparison = previousValue && value
    ? {
        percent: Math.abs(((value - previousValue) / previousValue) * 100),
        isPositive: value >= previousValue
      }
    : null;

  // Dynamic sizing based on widget dimensions - scale aggressively
  const area = width * height; // Total grid cells
  const isSmall = area <= 12; // 3×4 or smaller
  const isMedium = area <= 30; // 5×6 or smaller
  const isLarge = area >= 48; // 6×8 or larger
  const isHuge = area >= 96; // 12×8 or larger

  // Calculate sizes - scale with area for better use of space
  const iconSize = isSmall ? 20 : isMedium ? 32 : isLarge ? 56 : isHuge ? 80 : 40;
  const valueSize = isSmall ? 'text-xl' : isMedium ? 'text-3xl' : isLarge ? 'text-6xl' : isHuge ? 'text-8xl' : 'text-5xl';
  const titleSize = isSmall ? 'text-xs' : isMedium ? 'text-base' : isLarge ? 'text-2xl' : isHuge ? 'text-4xl' : 'text-xl';
  const comparisonSize = isSmall ? 'text-xs' : isMedium ? 'text-base' : isLarge ? 'text-2xl' : isHuge ? 'text-4xl' : 'text-xl';
  const padding = isSmall ? 'p-2' : isMedium ? 'p-3' : isLarge ? 'p-6' : 'p-4';
  const gap = isSmall ? 'gap-2' : isMedium ? 'gap-3' : 'gap-4';

  // Reactive text colors based on background
  const isLight = isLightBackground(backgroundColor);
  const valueTextColor = backgroundColor ? (isLight ? 'text-gray-900' : 'text-white') : 'text-dashboard-primary';
  const titleTextColor = backgroundColor ? (isLight ? 'text-gray-500' : 'text-gray-300') : 'text-dashboard-secondary';

  return (
    <div
      className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex items-center ${gap} overflow-hidden`}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      {/* Icon - Left Side, Vertically Centered */}
      <div className="flex items-center justify-center flex-shrink-0">
        <div className={`rounded-xl ${isLarge ? 'p-3' : 'p-2'}`} style={{ backgroundColor: color + '20' }}>
          <IconComponent size={iconSize} strokeWidth={2} style={{ color }} />
        </div>
      </div>

      {/* Content - Stacked: Value, Title */}
      <div className="flex-1 flex flex-col justify-center min-w-0">
        {/* Value + Comparison (Same Line, Vertically Centered) */}
        <div className="flex items-center gap-2">
          <div className={`${valueSize} font-bold ${valueTextColor} leading-none`}>
            {unit === '$' ? '$' : ''}{formatNumber(value)}{unit === '%' ? '%' : ''}
          </div>
          {comparison && (
            <div className={`${comparisonSize} font-bold leading-none ${comparison.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {comparison.isPositive ? '↑' : '↓'}{comparison.percent.toFixed(1)}%
            </div>
          )}
        </div>

        {/* Title Below Value */}
        <div className={`${titleSize} font-medium ${titleTextColor} truncate leading-tight ${isSmall ? 'mt-0.5' : 'mt-1'}`}>
          {title}
        </div>
      </div>
    </div>
  );
}
